import type {
  AudienceSegmentEntry,
  AudienceSegmentSearchInputDescriptor,
  AudienceSegmentSet,
  CumulativeSegment,
} from '@openx/types/gqlTypes';
import { createContext, useContext, useMemo, useState } from 'react';
import type { DealPromptDomainsContextValue, DealPromptDomainsProviderProps } from './type';

const DealPromptDomainsContext = createContext<DealPromptDomainsContextValue>({
  useDealPromptDomainSearchFetch: () => {
    throw new Error(
      'useDealPromptDomainSearchFetch: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptAppsSearchFetch: () => {
    throw new Error(
      'useDealPromptAppsSearchFetch: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptDomainSearchExtendedFetch: () => {
    throw new Error(
      'useDealPromptDomainSearchFetch: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptAppsSearchExtendedFetch: () => {
    throw new Error(
      'useDealPromptAppsSearchExtendedFetch: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptFilters: () => {
    throw new Error('useDealPromptFilters: Not implemented. Ensure you are inside the DealPromptDomainsProvider');
  },
  useDealPromptAudienceSearchTextFetch: () => {
    throw new Error(
      'useDealPromptAudienceSearchTextFetch: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptAudienceSearchFetch: () => {
    throw new Error(
      'useDealPromptAudienceSearchFetch: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  cumulativeData: null,
  numberOfResults: null,
  audienceSegments: null,
  setAudienceSegments: () => {
    throw new Error('setAudienceSegments: Not implemented. Ensure you are inside the DealPromptDomainsProvider');
  },
  audienceSegmentsSets: null,
  setAudienceSegmentsSets: () => {
    throw new Error('setAudienceSegmentsSets: Not implemented. Ensure you are inside the DealPromptDomainsProvider');
  },
  generatedSearchInput: undefined,
  setGeneratedSearchInput: () => {
    throw new Error('setGeneratedSearchInput: Not implemented. Ensure you are inside the DealPromptDomainsProvider');
  },
  setNumberOfResults: () => {
    throw new Error('setDomains: Not implemented. Ensure you are inside the DealPromptDomainsProvider');
  },
  setCumulativeData: () => {
    throw new Error('setCumulativeData: Not implemented. Ensure you are inside the DealPromptDomainsProvider');
  },
  useDealPromptLocationOptions: () => {
    throw new Error(
      'useDealPromptLocationOptions: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptRegionOptions: () => {
    throw new Error('useDealPromptRegionOptions: Not implemented. Ensure you are inside the DealPromptDomainsProvider');
  },
  useDealPromptCountryOptions: () => {
    throw new Error(
      'useDealPromptCountryOptions: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptDataPartnerOptions: () => {
    throw new Error(
      'useDealPromptDataPartnerOptions: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptAudienceAccountOptions: () => {
    throw new Error(
      'useDealPromptAccountOptions: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptTaxonomyMetadataOptions: () => {
    throw new Error(
      'useDealPromptTaxonomyMetadataOptions: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptAudienceExternalPartnerWithData: () => {
    throw new Error(
      'useDealPromptAudienceExternalPartnerWithData: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptAudienceExcelGenerate: () => {
    throw new Error(
      'useDealPromptAudienceExcelGenerate: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptAudienceCalculateOrf: () => {
    throw new Error(
      'useDealPromptAudienceCalculateOrf: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptPricing: () => {
    throw new Error('useDealPromptPricing: Not implemented. Ensure you are inside the DealPromptDomainsProvider');
  },
  useDealPromptOrganizationsList: () => {
    throw new Error(
      'useDealPromptOrganizationsList: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
  useDealPromptAudienceCreateDealSubmit: () => {
    throw new Error(
      'useDealPromptAudienceCreateDealSubmit: Not implemented. Ensure you are inside the DealPromptDomainsProvider'
    );
  },
});

export const useDealPromptDomainsContext = () => useContext(DealPromptDomainsContext);

export const DealPromptDomainsProvider = ({ children, dealPromptDomainsConfig }: DealPromptDomainsProviderProps) => {
  const [cumulativeData, setCumulativeData] = useState<CumulativeSegment[] | null>(null);
  const [numberOfResults, setNumberOfResults] = useState<number | null>(null);
  const [audienceSegments, setAudienceSegments] = useState<AudienceSegmentEntry[] | null>(null);
  const [audienceSegmentsSets, setAudienceSegmentsSets] = useState<AudienceSegmentSet[] | null>(null);
  const [generatedSearchInput, setGeneratedSearchInput] = useState<AudienceSegmentSearchInputDescriptor | undefined>(
    undefined
  );

  const value = useMemo<DealPromptDomainsContextValue>(
    () => ({
      ...dealPromptDomainsConfig,
      cumulativeData,
      setCumulativeData,
      setNumberOfResults,
      numberOfResults,
      audienceSegments,
      setAudienceSegments,
      audienceSegmentsSets,
      setAudienceSegmentsSets,
      generatedSearchInput,
      setGeneratedSearchInput,
    }),
    [
      dealPromptDomainsConfig,
      numberOfResults,
      cumulativeData,
      audienceSegments,
      audienceSegmentsSets,
      generatedSearchInput,
    ]
  );

  return <DealPromptDomainsContext.Provider value={value}>{children}</DealPromptDomainsContext.Provider>;
};
